import { reais } from "../../api/lib/moeda";

/** R$ da casa: delega ao formatador único (src/api/lib/moeda.ts, item 43 C54). */
export const money = (v: number | null | undefined) => reais(v);

/**
 * Converte string de data em Date SEM bug de fuso.
 * "2026-06-25" (data pura) é parseado pelo JS como meia-noite UTC e, em fusos
 * negativos (Brasília UTC-3), exibido como o dia ANTERIOR (24/06). Aqui datas
 * puras (YYYY-MM-DD) viram Date LOCAL (ano, mês, dia). Timestamps completos
 * (com hora/Z, ex: created_at) seguem o parse normal.
 */
function parseData(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(iso);
}

/**
 * O relógio da casa. 👑 28/08: "todo horário na tela é São Paulo, nada é fora".
 *
 * 🔑 `"pt-BR"` fixa o FORMATO (dia/mês), NÃO o relógio. Sem `timeZone`, a hora
 * exibida é a do APARELHO de quem abre a tela — no computador dele, em São
 * Paulo, aparece certo; num celular com fuso errado aparece outra hora **com
 * cara de brasileira**, que é o pior tipo de erro porque não tem cara de erro.
 */
const FUSO = "America/Sao_Paulo";

/**
 * ⚠️ DATA PURA NÃO LEVA FUSO, e fixar São Paulo nela QUEBRARIA.
 * `parseData` monta "2026-06-25" como Date LOCAL (meia-noite do aparelho).
 * Formatar sem `timeZone` devolve os mesmos ano/mês/dia em qualquer lugar do
 * mundo, porque construção e formatação usam o mesmo relógio e se cancelam.
 * Se aqui entrasse `timeZone: FUSO`, num aparelho a LESTE (ex.: Europa, UTC+2)
 * a meia-noite local viraria o DIA ANTERIOR em Brasília — exatamente o bug que
 * o comentário do `parseData` acima diz estar evitando.
 * Timestamp (com hora) é outra história: aí o instante é absoluto e PRECISA ser
 * lido no relógio de São Paulo.
 */
export const date = (iso?: string | null) => {
  if (!iso) return "—";
  const dataPura = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    ...(dataPura ? {} : { timeZone: FUSO }),
  }).format(parseData(iso));
};

export const dateTime = (iso?: string | null) =>
  iso
    ? new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: FUSO }).format(new Date(iso))
    : "—";

/**
 * Só a HORA, lida no relógio de São Paulo.
 *
 * 🟥 Existe porque três telas do Operador faziam
 * `d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })` sem
 * `timeZone`. Como diz o comentário do FUSO acima: `"pt-BR"` fixa o FORMATO,
 * não o relógio — num aparelho com fuso errado a hora sai diferente e **com
 * cara de brasileira**, que é o pior tipo de erro porque não tem cara de erro.
 */
export const hora = (iso?: string | null) =>
  iso
    ? new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: FUSO }).format(new Date(iso))
    : "—";

/**
 * O DIA CIVIL (AAAA-MM-DD) de um instante, no relógio de São Paulo.
 *
 * 🟥 É a peça que faltava para responder "isto é de hoje?". As telas
 * comparavam com `toDateString()` ou `setHours(0,0,0,0)` — ambos no fuso do
 * APARELHO. Um celular em outro fuso chamaria de "ontem" o que aconteceu hoje.
 *
 * 🔑 `en-CA` devolve AAAA-MM-DD, que ordena e compara como texto — sem montar
 * `Date` intermediário, que é justamente onde o fuso costuma se perder.
 *
 * ⚠️ NÃO use em data pura ("2026-08-25"): ela não tem instante, e o
 * comentário de `date()` acima explica por que fixar fuso nela quebraria.
 */
export const diaEmSaoPaulo = (instante: Date | string): string => {
  const d = typeof instante === "string" ? new Date(instante) : instante;
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: FUSO }).format(d);
};

/**
 * O MÊS CORRENTE ("AAAA-MM") EM SÃO PAULO. 🔴 30/09/2026: `new Date().toISOString().slice(0, 7)` é o mês em UTC —
 * depois das 21h do último dia já é o mês seguinte, e a ata de OUTUBRO foi gravada no lugar da de setembro.
 */
export const mesEmSaoPaulo = (agora: Date = new Date()): string => diaEmSaoPaulo(agora).slice(0, 7);

/**
 * O DIA CIVIL (AAAA-MM-DD) de um CARIMBO DO BANCO, seja ele data pura ou instante. 🔴 A18 (auditoria 3, 13/10): telas da
 * Regula faziam `carimbo.slice(0, 10)` em `timestamptz` — o dia em UTC: a defesa protocolada às 22h de SP aparecia no dia
 * seguinte. Data pura ("2026-08-25") volta como está (sem `Date`, ver `date()`); instante vira o dia de São Paulo; o resto, "".
 */
export const diaCivilDe = (carimbo?: string | null): string => {
  if (!carimbo) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(carimbo)) return carimbo;
  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(carimbo)) return "";
  // o Postgres escreve "2026-10-13 01:00:00+00": o `Date` do JS só lê com "T" e offset "+00:00"
  return diaEmSaoPaulo(carimbo.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00"));
};

/** dd/mm/aaaa do carimbo (data pura ou instante, no dia de SP); "—" sem carimbo válido. */
export const dataCivilBr = (carimbo?: string | null): string => {
  const d = diaCivilDe(carimbo);
  return d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "—";
};

/** Date.UTC da meia-noite do DIA DE SÃO PAULO de `agora` — base para contar dias entre datas sem depender do fuso do aparelho. */
export const baseUtcDoDiaEmSaoPaulo = (agora: Date): number => {
  const [a, m, d] = diaEmSaoPaulo(agora).split("-").map(Number);
  return Date.UTC(a, m - 1, d);
};

/**
 * Dias de calendário entre o DIA DE SP de `agora` e o dia civil de `vence_em` (data pura como está; instante no dia de SP).
 * 🔴 Codex P2 (#2971): a base do relógio dos documentos passou a ser o dia de SP, mas `docStatus` e `proximosRoteiros` seguiam
 * no dia do aparelho/UTC — às 23h30 de SP (02h30 UTC) a mesma linha saía "vencido" com "Vencido há 0 dias". Uma régua só. NaN se inválido.
 */
export const diasAteODiaCivil = (vence_em: string, agora: Date = new Date()): number => {
  const d = diaCivilDe(vence_em);
  if (!d) return NaN;
  return Math.round((Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10))) - baseUtcDoDiaEmSaoPaulo(agora)) / 86_400_000);
};

/** dd/mm do dia de SP de `hoje` + `dias` (o prazo curto que o síndico lê). Uma vez — antes eram 2 cópias no fuso do aparelho. */
export const diaMaisEmSaoPaulo = (dias: number, hoje: Date): string => {
  const d = new Date(baseUtcDoDiaEmSaoPaulo(hoje) + dias * 86_400_000);
  return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

export const pct = (v: number | null | undefined) => `${(v ?? 0).toFixed(1).replace(".", ",")}%`;

/**
 * A variação percentual de um mês contra o outro, com sinal: "+1,7%", "-0,5%". B5 (21/10): R$ 2,30 em R$ 10.875 é
 * -0,02%, e a tela escrevia "(-0%)" e "(0%)" para R$ 1,44 a mais. Abaixo de 0,1% em módulo diz "menos de 0,1%".
 */
export function pctDaVariacao(v: number | null | undefined): string | null {
  if (v == null || !Number.isFinite(v)) return null;
  const r = Math.round(v * 10) / 10;
  if (r === 0) return "menos de 0,1%";
  return `${r > 0 ? "+" : "-"}${Math.abs(r).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/**
 * Núcleo de parse/format do <MoneyInput/>. Vive aqui (módulo puro, sem React)
 * pra poder ser testado direto — teste que importa .tsx arrasta o mundo junto.
 */

/** Formata centavos (123450) pra display BR ("1.234,50"). Zero vira "" (placeholder aparece). */
export const moneyDeCentavos = (centavos: number): string => {
  if (!Number.isFinite(centavos) || centavos === 0) return "";
  return (centavos / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Converte o que o usuário digitou em centavos (inteiro). Tira tudo que não é
 * dígito — é o comportamento de caixa eletrônico: "193000" → 193000 centavos
 * → "1.930,00". Assim vírgula e ponto do usuário nunca gravam valor errado.
 */
export const centavosDeTexto = (raw: string): number => {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return 0;
  return Number(digits);
};

/**
 * Dias daqui até a data.
 *
 * 🚤 `agora` EXISTE PRA PODER SER INJETADO. Sem ele, toda função que
 * dependesse desta ficava impossível de testar com relógio fixo — e foi
 * exatamente o que aconteceu: `relogioDosDocumentos(docs, hoje)` recebia um
 * relógio, passava por `docStatus`, e aqui dentro o relógio era JOGADO FORA e
 * trocado pelo dia de verdade. A suíte passava ou falhava conforme a data em
 * que era rodada, e em 25/08/2026 ela ficou vermelha sozinha, sem ninguém
 * mudar uma linha.
 */
export const daysUntil = (iso: string, agora?: Date) => {
  // Compara meia-noite local do alvo com meia-noite local de hoje — contagem
  // de dias estável, sem o deslocamento de fuso das datas puras.
  const alvo = parseData(iso);
  const a = new Date(alvo.getFullYear(), alvo.getMonth(), alvo.getDate()).getTime();
  const hoje = agora ?? new Date();
  const h = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();
  return Math.round((a - h) / (1000 * 60 * 60 * 24));
};

/**
 * Data só-dia ("2025-08-26") parseada como UTC volta 1 dia no fuso BR; força
 * meia-noite local pra exibir o dia certo (não some 1 dia).
 *
 * Saiu de `pages/Folha.tsx` em 14/08/2026, quando o `GuiasCard` foi extraído
 * pra um módulo neutro: a tela v1 e o card passaram a precisar do MESMO
 * formatador, e deixá-lo na tela v1 recriaria a corda que a extração cortou.
 */
export function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const dataPura = iso.length <= 10;
  const d = new Date(dataPura ? `${iso}T00:00:00` : iso);
  if (isNaN(d.getTime())) return "—";
  // Mesma regra do `date()` acima: data pura já vira meia-noite LOCAL e não
  // leva fuso; timestamp é instante absoluto e é lido no relógio de São Paulo.
  // 🔑 Era daqui que saía o "fuso misto" da folha à noite (26 de agosto ×
  // período 27/08) — depois das 21h, o timestamp em UTC já era o dia seguinte.
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    ...(dataPura ? {} : { timeZone: FUSO }),
  }).format(d);
}

/**
 * O dia curto — "31/08". Nasceu dentro do MaoDeObraModal e o card do resultado
 * precisou do mesmo formato; duas cópias da mesma formatação é como duas telas
 * começam a escrever a mesma data de jeitos diferentes.
 *
 * Lê a string ISO SEM passar por `new Date`: "2026-08-31" vira Date à meia-noite
 * UTC e, em São Paulo, imprime 30/08. Data de trabalho é um dia do calendário,
 * não um instante.
 */
export const diaCurto = (iso?: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? "").slice(0, 10));
  return m ? `${m[3]}/${m[2]}` : "—";
};

/**
 * A saudação pela hora de São Paulo (régua da casa: horário é sempre SP).
 * B5 (21/10): Renovações dizia "Bom dia" às 23h40 (texto fixo) e a Torre usava a hora do navegador.
 */
export function saudacaoSP(agora: Date = new Date()): "Bom dia" | "Boa tarde" | "Boa noite" {
  const h = Number(new Intl.DateTimeFormat("en-US", { timeZone: FUSO, hour: "numeric", hourCycle: "h23" }).format(agora));
  return h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
}
